import { Link } from "react-router-dom";
import { Copy, Check, ArrowCounterClockwise } from "@phosphor-icons/react";
import { fmtInt, STATUS_LABELS, devCountdown, orderCupsCount } from "@/lib/api";
import { parsePedidoNaLoja, orderSkus, emptyOrdersMessage } from "@/lib/orderUtils";
import MarketplaceBadge from "./MarketplaceBadge";
import OrderNfeCell from "./OrderNfeCell";

function SkuChips({ o }) {
  const skus = orderSkus(o);
  if (skus.length === 0) return <span className="text-zinc-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1 max-w-[140px]" title={skus.join(", ")}>
      {skus.slice(0, 3).map((s) => (
        <span key={s} className="font-mono text-[10px] border border-zinc-300 bg-zinc-50 px-1.5 py-0.5">{s}</span>
      ))}
      {skus.length > 3 && <span className="text-[10px] text-zinc-400 font-mono">+{skus.length - 3}</span>}
    </div>
  );
}

function DevBadge({ o }) {
  const dev = devCountdown(o);
  if (!dev.applies) return null;
  if (dev.days_remaining > 0) {
    return (
      <span
        className="text-[10px] font-bold uppercase tracking-widest bg-black text-white px-2 py-0.5"
        title="Prazo de desenvolvimento (5 dias)"
        data-testid={`row-dev-countdown-${o.bling_number}`}
      >
        {dev.days_remaining}d dev
      </span>
    );
  }
  return (
    <span
      className="text-[10px] font-bold uppercase tracking-widest bg-[#E60000] text-white px-2 py-0.5"
      data-testid={`row-dev-overdue-${o.bling_number}`}
    >
      DEV ATRASADO {dev.days_overdue}D
    </span>
  );
}

export default function OrdersTable({
  loading, orders, ordersCount,
  canAdmin, canViewNfe,
  selectedIds, onToggleOne, onToggleAll,
  onOpen, copiedCode, onCopyShopCode, marketplaceFilter,
  showDeleted, onRestore, restoringId,
}) {
  const showSelect = canAdmin && !showDeleted;
  const colSpan = (showSelect ? 10 : 9) + (canViewNfe ? 1 : 0);
  return (
    <div className="hidden md:block border border-black bg-white overflow-x-auto">
      <table className="table table-compact" data-testid="orders-table">
        <thead><tr>
          {showSelect && (
            <th className="w-10 text-center">
              <input
                type="checkbox"
                checked={ordersCount > 0 && selectedIds.length === ordersCount}
                ref={(el) => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < ordersCount; }}
                onChange={onToggleAll}
                data-testid="select-all-orders"
                aria-label="Selecionar todos"
              />
            </th>
          )}
          <th>Nº Pedido</th><th>Cliente</th><th>SKUs</th><th>Pedido na Loja</th><th>Marketplace</th><th>Categoria</th><th>Status</th><th className="text-center">Copos</th>{canViewNfe && <th className="text-center">NFe</th>}<th></th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={colSpan} className="text-center py-10 text-zinc-400">Carregando...</td></tr>}
          {!loading && orders.length === 0 && (
            <tr><td colSpan={colSpan} className="text-center py-10">
              <div className="overline text-zinc-400 mb-2">{showDeleted ? "SEM PEDIDOS EXCLUÍDOS" : "SEM PEDIDOS"}</div>
              <div className="text-zinc-500">{emptyOrdersMessage(showDeleted, marketplaceFilter)}</div>
            </td></tr>
          )}
          {orders.map((o) => {
            const shop = parsePedidoNaLoja(o);
            return (
            <tr key={o.id} className={`cursor-pointer ${selectedIds.includes(o.id) ? "bg-yellow-50" : ""}`} onClick={() => onOpen(o.id)} data-testid={`order-row-${o.bling_number}`}>
              {showSelect && (
                <td className="text-center" onClick={(e) => { e.stopPropagation(); onToggleOne(o.id); }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(o.id)}
                    onChange={() => onToggleOne(o.id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`select-order-${o.bling_number}`}
                    aria-label={`Selecionar pedido ${o.bling_number}`}
                  />
                </td>
              )}
              <td className="font-mono font-semibold">
                <div className="flex items-center gap-2 flex-wrap">
                  <span>#{o.bling_number}</span>
                  {o.marketplace_source && <MarketplaceBadge marketplace={o.marketplace_source} />}
                  {o.store_name && (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600" data-testid={`row-store-${o.bling_number}`}>
                      {o.store_name}
                    </span>
                  )}
                </div>
              </td>
              <td>{o.customer}</td>
              <td data-testid={`order-skus-${o.bling_number}`}><SkuChips o={o} /></td>
              <td data-testid={`store-order-${o.bling_number}`}>
                {shop.code ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onCopyShopCode(shop.code); }}
                      className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-zinc-300 hover:border-black hover:bg-black hover:text-white rounded transition-colors"
                      title={`Copiar ${shop.code}`}
                      data-testid={`copy-shop-code-${o.bling_number}`}
                    >
                      {copiedCode === shop.code
                        ? <Check size={11} weight="bold" />
                        : <Copy size={11} weight="bold" />}
                    </button>
                    <span className="font-mono text-xs">{shop.code}</span>
                  </div>
                ) : <span className="text-zinc-300">—</span>}
              </td>
              <td className="text-zinc-600">{o.marketplace}</td>
              <td className="text-zinc-600 text-xs">{o.category}</td>
              <td>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`dot ${o.production_status}`} />
                  <span className="text-sm">{STATUS_LABELS[o.production_status]}</span>
                  {showDeleted ? (
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-zinc-800 text-white px-2 py-0.5" data-testid={`row-deleted-badge-${o.bling_number}`}>
                      EXCLUÍDO
                    </span>
                  ) : (
                    <DevBadge o={o} />
                  )}
                </div>
                {showDeleted && o.deleted_at && (
                  <div className="text-[10px] text-zinc-500 mt-1" data-testid={`row-deleted-info-${o.bling_number}`}>
                    em {new Date(o.deleted_at).toLocaleDateString("pt-BR")}{o.deleted_by ? ` por ${o.deleted_by}` : ""}
                  </div>
                )}
              </td>
              <td className="text-center font-mono font-bold">{fmtInt(orderCupsCount(o))}</td>
              {canViewNfe && (
                <td className="text-center" onClick={(e) => e.stopPropagation()} data-testid={`order-nfe-cell-${o.bling_number}`}>
                  <OrderNfeCell orderId={o.id} orderNumber={o.bling_number} />
                </td>
              )}
              <td onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 justify-end">
                  {showDeleted && (
                    <button
                      type="button"
                      className="text-[10px] font-bold uppercase tracking-widest border border-black bg-black text-white px-2.5 py-1 hover:bg-zinc-700 disabled:opacity-50 whitespace-nowrap"
                      onClick={() => onRestore(o.id)}
                      disabled={restoringId === o.id}
                      data-testid={`restore-order-${o.bling_number}`}
                      title="Restaurar o pedido ao sistema (exige senha de colaborador)"
                    >
                      <ArrowCounterClockwise size={11} weight="bold" className="inline mr-1" />
                      {restoringId === o.id ? "Restaurando..." : "Restaurar"}
                    </button>
                  )}
                  <Link to={`/orders/${o.id}`} className="text-xs underline" data-testid={`order-open-${o.bling_number}`}>Abrir</Link>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
